// Types et fonctions pures du menu « Formulaires » (constructeur de formulaires joints aux dossiers).
// Miroir TypeScript de `AdminVisaTravelApp backend/formulaires/schema.js` (constantes, slugify) et des formes de
// réponse de `formulaires/routes.js` (ModeleResume, Modele, Instance…). Aucune dépendance : ni axios, ni React.
// Le constructeur doit produire des définitions acceptées par `validerDefinition` côté backend :
//  - attributs propres à un type refusés sur un autre type (nettoyerAttributsParType) ;
//  - `valeur` ABSENTE avec les opérateurs rempli / vide (nettoyerCondition) ;
//  - une section contient au moins une question ; ids uniques (sections ET questions confondues) ; cible d'une
//    condition antérieure et jamais de type `info` (questionsAnterieures).

// ---------------------------------------------------------------------------
// Définition d'un formulaire (formulaires_modeles.definition, snapshot dans formulaires_instances.definition)
// ---------------------------------------------------------------------------

export type TypeQuestion =
  | 'texte' | 'texte_long' | 'nombre' | 'date' | 'oui_non' | 'choix' | 'choix_multiple' | 'fichier' | 'info';

export type Operateur = 'egal' | 'different' | 'parmi' | 'pas_parmi' | 'contient' | 'contient_pas' | 'rempli' | 'vide';

export type FormatTexte = 'email' | 'telephone';

export type ExtensionFichier = 'pdf' | 'jpg' | 'jpeg' | 'png' | 'heic' | 'doc' | 'docx';

export interface Option {
  valeur: string;  // ^[a-z0-9_-]{1,40}$, unique dans la question (générée par slugify depuis le libellé)
  libelle: string; // 1-300 caractères
}

// « Afficher seulement si <question> <op> <valeur> ». `valeur` : absente avec rempli/vide, tableau avec parmi/pas_parmi,
// chaîne sinon (toujours des chaînes, même pour une cible nombre : '1950').
export interface Condition {
  question: string;
  op: Operateur;
  valeur?: string | string[];
}

// Interface unique pour les 9 types : les attributs propres à un type sont optionnels et DOIVENT être absents sur les
// autres types (le backend les refuse). `aide` et `obligatoire` sont toujours présents dans une définition normalisée.
export interface Question {
  id: string;               // ^[a-z0-9_]{1,40}$, unique dans toute la définition, généré une fois (non modifiable ensuite)
  type: TypeQuestion;
  libelle: string;          // 1-300
  aide: string;             // 0-500
  obligatoire: boolean;     // toujours false pour info
  condition?: Condition;
  options?: Option[];       // choix, choix_multiple : 1-50 options
  accept?: string[];        // fichier : sous-ensemble de EXTENSIONS_AUTORISEES (défaut : liste entière)
  max_fichiers?: number;    // fichier : 1..5 (défaut 1)
  min?: number;             // nombre
  max?: number;             // nombre ; texte (1..2000, défaut 2000) ; texte_long (1..5000, défaut 5000)
  format?: FormatTexte;     // texte uniquement
}

export interface Section {
  id: string;               // même regex et même espace d'unicité que les questions
  titre: string;            // 1-300
  condition?: Condition;    // ne vise qu'une question d'une section STRICTEMENT antérieure
  questions: Question[];    // au moins une
}

export interface Definition {
  schema: 1;
  intro: string;            // 0-2000
  message_fin: string;      // 0-2000
  sections: Section[];      // 1-30
}

// ---------------------------------------------------------------------------
// Réponses du client (formulaires_instances.reponses, écrites UNIQUEMENT par le backend client ; l'admin les affiche
// sans rien évaluer)
// ---------------------------------------------------------------------------

export interface FichierReponse {
  nom: string;          // nom réel sur disque : form-<question>-<ts>-<rand6>.<ext>
  nom_original: string;
  taille: number;
  date: string;         // ISO
}

export type ValeurReponse = string | string[]; // string[] pour choix_multiple

export interface Reponses {
  valeurs: Record<string, ValeurReponse>;
  fichiers: Record<string, FichierReponse[]>;
  visibles: string[];
  sections_visibles: string[];
  manquantes: string[];
  section_courante: string | null;
  calcule_le: string;
}

// ---------------------------------------------------------------------------
// Instances (formulaires_instances) — formes renvoyées par /formulaires/dossiers/*
// ---------------------------------------------------------------------------

export type StatutInstance = 'cree' | 'envoye' | 'en_cours' | 'soumis' | 'annule';

export type EvenementJournal =
  | 'creation' | 'envoi' | 'relance' | 'envoi_non_confirme' | 'premiere_sauvegarde' | 'sauvegarde'
  | 'fichier' | 'fichier_retire' | 'soumission' | 'reouverture' | 'annulation';

export interface EntreeJournal {
  d: string;             // ISO
  e: EvenementJournal;
  x: string | null;      // détail (e-mail, nom de fichier, erreur Brevo…)
}

// Les dates MySQL arrivent sérialisées en ISO (ou null)
export interface Instance {
  id: number;
  id_demande: number;
  chemin: string;
  id_modele: number | null;
  version_modele: number | null;
  nom_modele: string;
  definition: Definition;
  jeton: string | null;  // null : lu par un compte société en lecture seule (le lien du client n'est pas transmis)
  url: string | null;
  email_destinataire: string;
  statut: StatutInstance;
  reponses: Reponses | null;
  version_reponses: number;
  nb_envois: number;
  date_envoi: string | null;
  date_sauvegarde: string | null;
  date_soumission: string | null;
  journal: EntreeJournal[];
  date_creation: string;
  date_maj: string | null;
  fichiers_urls: Record<string, string>; // nom du fichier -> URL https://common.visamonde.com/demandes/<chemin>/<nom>
}

// GET /formulaires/dossiers/par-chemin/:chemin
export interface DossierFormulaires {
  id_demande: number;
  chemin: string;
  prenom: string | null;
  nom: string | null;
  pays: string | null;
  email: string | null;
  source: string | null;   // 'admin_manuel' requis pour joindre un formulaire
  archived: number | null;
}

export interface InstancesParChemin {
  dossier: DossierFormulaires;
  instances: Instance[];   // id DESC
  url_publique_base: string;
}

// POST /formulaires/dossiers : { chemin } ou { id_demande }, + id_modele, + email facultatif (défaut : e-mail du dossier)
export interface CreerInstanceInput {
  chemin?: string;
  id_demande?: number;
  id_modele: number;
  email?: string;
}

// 201 de POST /formulaires/dossiers
export interface InstanceCreee {
  id: number;
  jeton: string;
  url: string;
  statut: 'cree';
  email_destinataire: string;
  nom_modele: string;
}

// ---------------------------------------------------------------------------
// Modèles (formulaires_modeles) — formes renvoyées par /formulaires/modeles/*
// ---------------------------------------------------------------------------

// Ligne de GET /formulaires/modeles
export interface ModeleResume {
  id: number;
  nom: string;
  description: string;
  version: number;
  actif: number;                             // 0 | 1
  nb_questions: number;                      // hors paragraphes info
  nb_instances: number;
  nb_instances_versions_anterieures: number; // instances dont version_modele < version courante
  date_creation: string;
  date_maj: string | null;
}

export type Modele = ModeleResume & { definition: Definition };

// Modèle proposé à la jonction (« Joindre un formulaire », « Nouveau dossier ») : GET …/modeles?actifs=1. Le routeur
// Legrand ne renvoie que ces champs ; l'admin renvoie un ModeleResume complet (compatible).
export type ModeleProposable = Pick<ModeleResume, 'id' | 'nom' | 'version' | 'actif' | 'nb_questions'>;

// Corps de POST /formulaires/modeles (PUT : Partial<ModeleInput>)
export interface ModeleInput {
  nom: string;
  description?: string;
  definition: Definition;
  actif?: boolean;
}

// ---------------------------------------------------------------------------
// Divers contrats HTTP
// ---------------------------------------------------------------------------

// GET /formulaires/etat
export interface EtatFormulaires {
  ok: boolean;
  raison: string | null;     // libellé de désactivation (tables absentes, MySQL injoignable)
  url_publique_base: string;
  dry_run: boolean;          // FORMULAIRES_MAIL_DRY_RUN=true : aucun mail réel
}

// Contexte du template formulaire_a_remplir (valeurs déjà échappées HTML)
export interface VariablesMailFormulaire {
  prenom: string;
  nom: string;
  pays: string;
  idDemande: string;
  nomFormulaire: string;
  urlFormulaire: string;
  relance: string;                 // 'oui' ou ''
  nbManquantes: string;            // '' si jamais sauvegardé
  dateDerniereSauvegarde: string;  // jj/mm/aaaa hh:mm ou ''
}

// GET /formulaires/dossiers/:id/apercu-mail
export interface ApercuMail {
  destinataire: string;
  sujet: string;
  corps_html: string;
  variables: VariablesMailFormulaire;
}

// Corps de POST /formulaires/dossiers/:id/envoyer (sujet + corps_html vides -> rendu du template côté serveur)
export interface EnvoiInput {
  email?: string;
  sujet?: string;
  corps_html?: string;
}

// 200 de POST /formulaires/dossiers/:id/envoyer
export interface ResultatEnvoi {
  id: number;
  statut: StatutInstance;
  nb_envois: number;
  date_envoi: string | null;
  email_destinataire: string;
  mail: {
    envoye: boolean;          // true sur un 200 (le refus explicite répond 502 { message, mail: { envoye: false, erreur } })
    indetermine: boolean;     // réponse Brevo non reçue : considéré envoyé, journal « envoi_non_confirme »
    messageId: string | null; // 'dry-run' en simulation
  };
}

// Erreur 400 de validation : { message, erreurs: ErreurValidation[] }, chemin du type 'sections[1].questions[2].condition.valeur'
export interface ErreurValidation {
  chemin: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Constantes (copies EXACTES de schema.js)
// ---------------------------------------------------------------------------

export const TYPES: TypeQuestion[] = ['texte', 'texte_long', 'nombre', 'date', 'oui_non', 'choix', 'choix_multiple', 'fichier', 'info'];
export const OPERATEURS: Operateur[] = ['egal', 'different', 'parmi', 'pas_parmi', 'contient', 'contient_pas', 'rempli', 'vide'];

// Opérateurs autorisés selon le type de la question CIBLE d'une condition. `info` n'est jamais référençable.
export const OPERATEURS_PAR_TYPE: Record<TypeQuestion, Operateur[]> = {
  choix: ['egal', 'different', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  oui_non: ['egal', 'different', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  texte: ['egal', 'different', 'contient', 'contient_pas', 'rempli', 'vide'],
  texte_long: ['egal', 'different', 'contient', 'contient_pas', 'rempli', 'vide'],
  nombre: ['egal', 'different', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  date: ['egal', 'different', 'rempli', 'vide'],
  choix_multiple: ['contient', 'contient_pas', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  fichier: ['rempli', 'vide'],
  info: [],
};

// Opérateurs dont la `valeur` est un tableau / absente
export const OPERATEURS_LISTE: Operateur[] = ['parmi', 'pas_parmi'];
export const OPERATEURS_SANS_VALEUR: Operateur[] = ['rempli', 'vide'];

// Liste blanche des extensions d'une question fichier (défaut : la liste entière)
export const EXTENSIONS_AUTORISEES: ExtensionFichier[] = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'doc', 'docx'];

export const LIMITES = {
  sections: 30,             // sections par définition
  questions: 150,           // questions au total (toutes sections confondues)
  options: 50,              // options par question choix / choix_multiple
  tailleDefinition: 300000, // JSON.stringify(definition).length
  libelle: 300,             // libellé de question, titre de section, libellé d'option
  aide: 500,
  intro: 2000,              // intro et message_fin
  texteMax: 2000,           // `max` d'une question texte
  texteLongMax: 5000,       // `max` d'une question texte_long
  maxFichiers: 5,           // `max_fichiers` d'une question fichier
  nom: 160,                 // nom du modèle (routes.js NOM_MAX)
  description: 500,         // description du modèle (routes.js DESCRIPTION_MAX)
  valeurTexte: 200,         // `valeur` d'une condition sur texte / texte_long
};

export const REGEX = {
  id: /^[a-z0-9_]{1,40}$/,            // id de section ou de question
  valeurOption: /^[a-z0-9_-]{1,40}$/, // valeur d'une option
  numerique: /^-?\d+(\.\d+)?$/,       // `valeur` de condition sur une cible nombre
  dateIso: /^\d{4}-\d{2}-\d{2}$/,     // `valeur` de condition sur une cible date
};

// ---------------------------------------------------------------------------
// Libellés (fr) pour l'interface
// ---------------------------------------------------------------------------

export const LIBELLES_TYPES: Record<TypeQuestion, string> = {
  texte: 'Texte court',
  texte_long: 'Texte long',
  nombre: 'Nombre',
  date: 'Date',
  oui_non: 'Oui / Non',
  choix: 'Choix unique',
  choix_multiple: 'Choix multiple',
  fichier: 'Fichier à joindre',
  info: "Paragraphe d'information",
};

export const LIBELLES_OPERATEURS: Record<Operateur, string> = {
  egal: 'est égal à',
  different: 'est différent de',
  parmi: 'est parmi',
  pas_parmi: "n'est pas parmi",
  contient: 'contient',
  contient_pas: 'ne contient pas',
  rempli: 'est renseigné',
  vide: 'est vide',
};

export const LIBELLES_STATUTS: Record<StatutInstance, string> = {
  cree: 'Créé',
  envoye: 'Envoyé',
  en_cours: 'En cours',
  soumis: 'Soumis',
  annule: 'Annulé',
};

export const LIBELLES_FORMATS: Record<FormatTexte, string> = {
  email: 'E-mail',
  telephone: 'Téléphone',
};

export const LIBELLES_EVENEMENTS: Record<EvenementJournal, string> = {
  creation: 'Créé',
  envoi: 'Envoyé',
  relance: 'Relancé',
  envoi_non_confirme: 'Envoi non confirmé',
  premiere_sauvegarde: 'Première sauvegarde',
  sauvegarde: 'Sauvegarde',
  fichier: 'Fichier déposé',
  fichier_retire: 'Fichier retiré',
  soumission: 'Soumis',
  reouverture: 'Rouvert',
  annulation: 'Annulé',
};

// Options fixes d'une question oui_non (pour les Select de valeur d'une condition)
export const OPTIONS_OUI_NON: Option[] = [
  { valeur: 'oui', libelle: 'Oui' },
  { valeur: 'non', libelle: 'Non' },
];

// ---------------------------------------------------------------------------
// Identifiants
// ---------------------------------------------------------------------------

// Copie fidèle de schema.js : NFD sans accents, minuscules, [^a-z0-9]+ -> '_', '_' de bord retirés, 40 caractères max,
// préfixé (`q_` questions, `s_` sections) si vide ou commençant par un chiffre.
//   slugify('Société qui vous invite') -> 'societe_qui_vous_invite' ; slugify('2 roues') -> 'q_2_roues'
export const slugify = (libelle: string | null | undefined, prefixe: string = 'q_'): string => {
  let s = String(libelle === null || libelle === undefined ? '' : libelle)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 40).replace(/_+$/, '');
  if (!s || /^[0-9]/.test(s)) s = (prefixe + s).slice(0, 40);
  return s;
};

// `base` s'il est libre, sinon base_2, base_3… (la base est tronquée pour rester dans 40 caractères)
export const idUnique = (base: string, idsExistants: string[]): string => {
  if (idsExistants.indexOf(base) < 0) return base;
  for (let n = 2; ; n++) {
    const suffixe = `_${n}`;
    const candidat = base.slice(0, 40 - suffixe.length).replace(/_+$/, '') + suffixe;
    if (idsExistants.indexOf(candidat) < 0) return candidat;
  }
};

// Tous les ids d'une définition (sections ET questions : même espace d'unicité)
export const tousLesIds = (def: Definition): string[] => {
  const ids: string[] = [];
  def.sections.forEach((s) => {
    ids.push(s.id);
    s.questions.forEach((q) => ids.push(q.id));
  });
  return ids;
};

// Chemins des erreurs 400 (clés de `erreurs` dans QuestionCardProps)
export const cheminSection = (i: number): string => `sections[${i}]`;
export const cheminQuestion = (i: number, j: number): string => `sections[${i}].questions[${j}]`;

// Sous-ensemble des erreurs portant sur `prefixe` (lui-même, ou un de ses sous-chemins '.x' / '[k]') ;
// 'sections[0].questions[1]' ne capte pas 'sections[0].questions[10]'.
export const filtrerErreurs = (erreurs: Record<string, string>, prefixe: string): Record<string, string> => {
  const r: Record<string, string> = {};
  Object.keys(erreurs).forEach((k) => {
    if (k === prefixe || k.indexOf(prefixe + '.') === 0 || k.indexOf(prefixe + '[') === 0) r[k] = erreurs[k];
  });
  return r;
};

// ---------------------------------------------------------------------------
// Fabriques
// ---------------------------------------------------------------------------

// Question neuve du type demandé, avec les défauts de schema.js (texte max 2000, texte_long max 5000, fichier : toutes
// les extensions + 1 fichier ; choix : options vides, à compléter). Id : slugify du libellé (ou 'q_<type>' sans libellé),
// dédoublonné par rapport à idsExistants (utiliser tousLesIds(def)).
export const nouvelleQuestion = (type: TypeQuestion, libelle: string | undefined, idsExistants: string[]): Question => {
  const base = libelle && libelle.trim() ? slugify(libelle) : `q_${type}`;
  const q: Question = { id: idUnique(base, idsExistants), type, libelle: libelle || '', aide: '', obligatoire: false };
  return nettoyerAttributsParType(q);
};

// Option neuve : valeur générée depuis le libellé (préfixe 'o_' si vide ou chiffre initial), unique dans la question
export const nouvelleOption = (libelle: string, valeursExistantes: string[]): Option => ({
  valeur: idUnique(slugify(libelle, 'o_'), valeursExistantes),
  libelle,
});

// Section neuve, sans question (le backend en exige au moins une à l'enregistrement). Id : slugify du titre
// (préfixe 's_'), ou 'section' sans titre, dédoublonné.
export const nouvelleSection = (idsExistants: string[], titre: string = ''): Section => ({
  id: idUnique(titre.trim() ? slugify(titre, 's_') : 'section', idsExistants),
  titre,
  questions: [],
});

// Définition de départ d'un nouveau modèle : une section « Section 1 » vide (id 'section_1')
export const definitionVide = (): Definition => ({
  schema: 1,
  intro: '',
  message_fin: '',
  sections: [nouvelleSection([], 'Section 1')],
});

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

// Question par id dans toute la définition (première occurrence), ou undefined
export const trouverQuestion = (def: Definition, id: string): Question | undefined => {
  for (let i = 0; i < def.sections.length; i++) {
    const qs = def.sections[i].questions;
    for (let j = 0; j < qs.length; j++) if (qs[j].id === id) return qs[j];
  }
  return undefined;
};

// Questions référençables par une condition (jamais les paragraphes info) :
//  - pour une question (cible.question défini) : celles qui la précèdent dans sa section + toutes celles des sections
//    précédentes ;
//  - pour une section (cible.question absent) : celles des sections STRICTEMENT antérieures.
// C'est la règle d'antériorité que schema.js vérifie (400) : le Select du ConditionEditor ne propose que cette liste.
export const questionsAnterieures = (def: Definition, cible: { section: number; question?: number }): Question[] => {
  const r: Question[] = [];
  def.sections.forEach((s, i) => {
    if (i > cible.section) return;
    if (i === cible.section && cible.question === undefined) return;
    s.questions.forEach((q, j) => {
      if (i === cible.section && j >= (cible.question as number)) return;
      if (q.type !== 'info') r.push(q);
    });
  });
  return r;
};

// Valeurs proposables pour `valeur` d'une condition selon la cible : options (choix, choix_multiple), Oui/Non
// (oui_non), null sinon (saisie libre : TextField).
export const optionsCible = (cible: Question): Option[] | null => {
  if (cible.type === 'choix' || cible.type === 'choix_multiple') return cible.options || [];
  if (cible.type === 'oui_non') return OPTIONS_OUI_NON;
  return null;
};

// Met la forme de `valeur` en accord avec l'opérateur (schema.js refuse une valeur avec rempli/vide, exige un
// tableau non vide avec parmi/pas_parmi et une chaîne sinon). À appeler à chaque changement d'opérateur.
export const nettoyerCondition = (c: Condition): Condition => {
  const n: Condition = { question: c.question, op: c.op };
  if (OPERATEURS_SANS_VALEUR.indexOf(c.op) >= 0) return n;
  if (OPERATEURS_LISTE.indexOf(c.op) >= 0) {
    if (Array.isArray(c.valeur)) n.valeur = c.valeur.slice();
    else if (c.valeur !== undefined && c.valeur !== '') n.valeur = [c.valeur];
    else n.valeur = [];
    return n;
  }
  if (Array.isArray(c.valeur)) { if (c.valeur.length) n.valeur = c.valeur[0]; }
  else if (c.valeur !== undefined) n.valeur = c.valeur;
  return n;
};

// Porteurs (sections et questions) dont la condition vise idQuestion : pour la confirmation avant suppression
export const porteursDeCondition = (
  def: Definition, idQuestion: string
): { type: 'section' | 'question'; id: string; libelle: string }[] => {
  const r: { type: 'section' | 'question'; id: string; libelle: string }[] = [];
  def.sections.forEach((s) => {
    if (s.condition && s.condition.question === idQuestion) r.push({ type: 'section', id: s.id, libelle: s.titre });
    s.questions.forEach((q) => {
      if (q.condition && q.condition.question === idQuestion) r.push({ type: 'question', id: q.id, libelle: q.libelle });
    });
  });
  return r;
};

// Copie de la définition sans les conditions qui visent idQuestion (après suppression de cette question)
export const retirerConditionsOrphelines = (def: Definition, idQuestion: string): Definition => ({
  ...def,
  sections: def.sections.map((s) => {
    const section: Section = {
      ...s,
      questions: s.questions.map((q) => {
        if (!q.condition || q.condition.question !== idQuestion) return q;
        const { condition, ...reste } = q;
        return reste;
      }),
    };
    if (section.condition && section.condition.question === idQuestion) delete section.condition;
    return section;
  }),
});

// Le texte lisible d'une condition (« « But du voyage » est égal à Affaires ») est decrireCondition de ./libelles :
// unique formulation pour l'aperçu, l'éditeur de condition et la fiche dossier.

// Porteurs dont la condition vise idQuestion AVEC la valeur d'option `valeur` (scalaire égale ou tableau la contenant) :
// pour la confirmation avant suppression d'une option de choix / choix_multiple
export const porteursDeValeur = (
  def: Definition, idQuestion: string, valeur: string
): { type: 'section' | 'question'; id: string; libelle: string }[] => {
  const vise = (c: Condition | undefined): boolean =>
    !!c && c.question === idQuestion && (Array.isArray(c.valeur) ? c.valeur.indexOf(valeur) >= 0 : c.valeur === valeur);
  const r: { type: 'section' | 'question'; id: string; libelle: string }[] = [];
  def.sections.forEach((s) => {
    if (vise(s.condition)) r.push({ type: 'section', id: s.id, libelle: s.titre });
    s.questions.forEach((q) => {
      if (vise(q.condition)) r.push({ type: 'question', id: q.id, libelle: q.libelle });
    });
  });
  return r;
};

// Copie de la définition où `valeur` est retirée des conditions qui visent idQuestion (après suppression de cette
// option) : retirée du tableau (parmi / pas_parmi), la condition entière disparaissant si elle ne porte plus rien.
export const retirerValeurDesConditions = (def: Definition, idQuestion: string, valeur: string): Definition => {
  // Condition ajustée, ou undefined si elle doit disparaître
  const ajuster = (c: Condition): Condition | undefined => {
    if (c.question !== idQuestion) return c;
    if (Array.isArray(c.valeur)) {
      const reste = c.valeur.filter((v) => v !== valeur);
      if (reste.length === c.valeur.length) return c;
      return reste.length ? { ...c, valeur: reste } : undefined;
    }
    return c.valeur === valeur ? undefined : c;
  };
  return {
    ...def,
    sections: def.sections.map((s) => {
      const section: Section = {
        ...s,
        questions: s.questions.map((q) => {
          if (!q.condition) return q;
          const c = ajuster(q.condition);
          if (c === q.condition) return q;
          const { condition, ...reste } = q;
          return c ? { ...reste, condition: c } : reste;
        }),
      };
      if (section.condition) {
        const c = ajuster(section.condition);
        if (c) section.condition = c; else delete section.condition;
      }
      return section;
    }),
  };
};

// ---------------------------------------------------------------------------
// Cohérence d'une question avec son type
// ---------------------------------------------------------------------------

// Copie de la question ne portant que les attributs de son type (schema.js refuse options hors choix/choix_multiple,
// accept/max_fichiers hors fichier, min hors nombre, max hors texte/texte_long/nombre, format hors texte), avec les
// défauts posés pour le type courant, `obligatoire` forcé à false pour info, et une condition mise en forme
// (nettoyerCondition). À appeler à chaque changement de type.
export const nettoyerAttributsParType = (q: Question): Question => {
  const n: Question = {
    id: q.id, type: q.type, libelle: q.libelle, aide: q.aide || '', obligatoire: q.type === 'info' ? false : !!q.obligatoire,
  };
  if (q.condition) n.condition = nettoyerCondition(q.condition);
  switch (q.type) {
    case 'choix':
    case 'choix_multiple':
      n.options = Array.isArray(q.options) ? q.options : [];
      break;
    case 'fichier': {
      const accept = Array.isArray(q.accept) ? q.accept.filter((e) => EXTENSIONS_AUTORISEES.indexOf(e as ExtensionFichier) >= 0) : [];
      n.accept = accept.length ? accept : EXTENSIONS_AUTORISEES.slice();
      const mf = q.max_fichiers;
      n.max_fichiers = mf !== undefined && Number.isInteger(mf) && mf >= 1 && mf <= LIMITES.maxFichiers ? mf : 1;
      break;
    }
    case 'nombre':
      if (q.min !== undefined && Number.isFinite(q.min)) n.min = q.min;
      if (q.max !== undefined && Number.isFinite(q.max)) n.max = q.max;
      break;
    case 'texte':
    case 'texte_long': {
      const plafond = q.type === 'texte' ? LIMITES.texteMax : LIMITES.texteLongMax;
      const max = q.max;
      n.max = max !== undefined && Number.isInteger(max) && max >= 1 && max <= plafond ? max : plafond;
      if (q.type === 'texte' && (q.format === 'email' || q.format === 'telephone')) n.format = q.format;
      break;
    }
    default:
      break;
  }
  return n;
};
